<template>
  <v-container fluid class="mt-3 mx-1">
    <v-form ref="form">
      <v-row>
        <v-col cols="12">
          <h2 v-if="edit !== true" class="primary--text font-weight-bold pt-5 pl-2">
            <v-icon color="primary" large left>settings</v-icon>
            {{ $t("settings/customer:edit.titles.create") }}
          </h2>
          <h2 v-else-if="edit === true" class="primary--text font-weight-medium">
            <v-icon color="primary" large left>settings</v-icon>
            {{ $t('common:general') }}
          </h2>

          <v-card elevation="0" flat tile>

            <v-tabs
                color="primary"
                centered
                ref="tabs"
                v-model="currentTab"
                :show-arrows="true"
            >
              <v-tab to="#general">
                <v-icon class="mr-3">home</v-icon>
                {{ $t('common:general') }}
              </v-tab>

              <v-tab to="#options" v-if="edit">
                <v-icon class="mr-3">router</v-icon>
                {{ $t('common:settings') }}
              </v-tab>
              <v-tab to="#users" v-if="edit">
                <v-icon class="mr-3">group</v-icon>
                {{ $t('common:user') }}
              </v-tab>
            </v-tabs>

            <v-tabs-items v-model="currentTab">
              <v-tab-item value="general" eager>
                <v-row justify="space-around">
                  <v-col cols="12" md="5">
                    <v-radio-group row v-model="customer.type" :rules="[ruleRequired($t('utils:rules.required'))]">
                      <v-radio :label="$t('common:customer')" value="CUSTOMER"/>
                      <v-radio :label="$t('settings/customer:edit.labels.supplier')" value="SUPPLIER"/>
                    </v-radio-group>

                    <!-- Name -->
                    <DuplicateCheckField
                        ref="duplName"
                        :value="customer.name"
                        :label="$t('common:name')"
                        item-additional-info-field="street"
                        required
                        tab-index="1"
                        item-text="name"
                        @input="customer.name = $event"
                        @checkForDuplicates="checkByName"
                        @clickedShowItem="showCustomer"
                    />

                    <v-text-field
                        outlined
                        v-model="customer.regNumber"
                        :label="$t('settings/customer:overview.registration-number')"
                        clearable
                        tabindex="2"
                    />
                    <h4 class="primary--text font-weight-bold">
                      {{ $t('common:address') }}
                    </h4>
                    <DuplicateCheckField
                        ref="duplAddress"
                        :value="customer.street"
                        :label="$t('common:street')"
                        item-additional-info-field="street"
                        tab-index="3"
                        item-text="name"
                        @input="customer.street = $event"
                        @checkForDuplicates="checkByAddress"
                        @clickedShowItem="showCustomer"
                    />

                    <v-row justify="space-around">
                      <v-col cols="6" md="2">
                        <v-text-field
                            outlined
                            v-model="customer.zip"
                            :label="$t('common:zip')"
                            clearable
                            tabindex="4"
                            @input="getCityProvinceByZIP"
                        />
                      </v-col>
                      <v-col cols="6" md="10">
                        <v-text-field
                            outlined
                            v-model="customer.city"
                            :label="$t('common:city')"
                            clearable
                            tabindex="5"
                        />
                      </v-col>
                    </v-row>
                    <!-- Input Select Country -->
                    <v-select
                        :items="restCountry"
                        item-text="translatedName"
                        item-value="id"
                        :menu-props="{ bottom: true, offsetY: true }"
                        v-model="customer.country"
                        outlined
                        :label="$t('common:country')"
                        clearable
                        :rules="[ruleRequired($t('utils:rules.required'))]"
                        @change="updateProvince"
                        tabindex="6"
                    />
                    <v-select
                        :items="restProvince"
                        item-text="translatedName"
                        item-value="id"
                        :menu-props="{ bottom: true, offsetY: true }"
                        v-model="customer.province"
                        outlined
                        :label="$t('common:province')"
                        clearable
                        tabindex="7"
                    />

                    <h4 class="primary--text font-weight-bold">
                      {{ $t('settings/customer:edit.labels.bank-account') }}
                    </h4>
                    <v-row justify="space-around">
                      <v-col cols="6" md="4">
                        <v-text-field
                            outlined
                            v-model="customer.bic"
                            :label="$t('settings/customer:edit.labels.bic')"
                            clearable
                            tabindex="8"
                        />
                      </v-col>
                      <v-col cols="6" md="8">
                        <v-text-field
                            outlined
                            v-model="customer.iban"
                            :label="$t('settings/customer:edit.labels.iban')"
                            clearable
                            tabindex="9"
                        />
                      </v-col>
                    </v-row>
                    <v-text-field
                        outlined
                        v-model="customer.vatId"
                        :label="$t('settings/customer:edit.labels.vatin')"
                        clearable
                        tabindex="10"
                    />
                  </v-col>
                  <v-col cols="12" md="5">
                    <h4 class="primary--text font-weight-bold">
                      {{ $t('settings/customer:edit.labels.contact') }}
                    </h4>
                    <v-text-field
                        outlined
                        v-model="customer.contactName"
                        :label="$t('settings/customer:common.contact-name')"
                        clearable
                        tabindex="11"
                    />
                    <v-text-field
                        outlined
                        v-model="customer.contactMail"
                        :label="$t('settings/customer:common.contact-mail')"
                        clearable
                        tabindex="12"
                    />
                    <v-text-field
                        outlined
                        v-model="customer.contactPhone"
                        :label="$t('settings/customer:common.contact-phone')"
                        clearable
                        tabindex="13"
                    />
                    <v-autocomplete
                        :items="restCustomer"
                        item-text="name"
                        item-value="id"
                        :menu-props="{ bottom: true, offsetY: true }"
                        v-model="customer.parentId"
                        outlined
                        :label="$t('settings/customer:edit.labels.parentCustomer')"
                        clearable
                        tabindex="14"
                    />
                    <TagComponent
                        :values="customer.tags"
                        @input="value => customer.tags = value"
                        :label="$t('common:tags')"
                    />
                    <TagComponent
                        :values="customer.blackTags"
                        @input="value => customer.blackTags = value"
                        :label="$t('common:black-tags')"
                    />
                    <!-- Provision -->
                    <v-text-field
                        outlined
                        append-icon="%"
                        v-model="customer.provision"
                        @change="(v) => customer.provision = getCorrectPercentage(v)"
                        :label="$t('settings/customer:edit.labels.provision')"
                        tabindex="17"
                    />
                    <v-row justify="space-around">
                      <v-col cols="4" md="4">
                        <v-checkbox
                            v-model="customer.advertiser"
                            :label="$t('settings/customer:edit.labels.advertiser')"
                            tabindex="18"/>
                      </v-col>
                      <v-col cols="4" md="4">
                        <v-checkbox
                            v-model="customer.inhouse"
                            :label="$t('settings/customer:edit.labels.inhouse')"
                            tabindex="19"/>
                      </v-col>
                      <v-col cols="4" md="4">
                        <v-checkbox
                            v-model="customer.facilityOperator"
                            :label="$t('common:customer')"
                            tabindex="20"/>
                      </v-col>
                    </v-row>
                  </v-col>
                </v-row>
              </v-tab-item>

              <v-tab-item value="options" eager v-if="edit">
                <ServerOptionForm
                    ref="serverOptionForm"
                    @updateOptions="updateOptions"
                />
              </v-tab-item>
              <v-tab-item value="users" eager v-if="edit">
                <UsersTab
                    ref="usersTab"
                    :customerId="id"
                />
              </v-tab-item>
            </v-tabs-items>
          </v-card>
        </v-col>
        <v-col cols="12">
          <v-card
              elevation="0"
              flat
              tile
              class="pl-3 pt-3 pb-md-3"
          >
            <BaseButton
                color="primary"
                :btn-text="$t('buttons:save')"
                @clickedButton="updateForm()"
            />
            <TwoButtonDialog
                v-if="edit"
                ref="deleteButton"
                :btn-text="$t('buttons:delete')"
                color="error"
                :dialog-title="$t('dialog:delete-btn-title', {name : $t('settings/customer:edit.dialogs.delete-customer-part-1')})"
                :dialog-text="$t('dialog:delete-btn-dialog', {name : $t('settings/customer:edit.dialogs.delete-customer-part-2')})"
                :button1-text="$t('buttons:cancel')"
                :button2-text="$t('buttons:delete')"
                @clickedButton1="$refs.deleteButton.showDialog = false"
                @clickedButton2="deleteCustomer()"
            />
            <DirtyConfirmButtonDialog
                ref="cancelButton"
                :btn-text="$t('buttons:back')"
                color="grey"
                :dialog-title="$t('dialog:dirty-save-btn-title')"
                :dialog-text="$t('dialog:dirty-save-btn-dialog')"
                :button1-text="$t('dialog:dirty-save-btn-1-text')"
                :button2-text="$t('dialog:dirty-save-btn-2-text')"
                @clickedButton="$router.push(`/${$store.state.manager.settingsModuleUrl}/customer`)"
                :dataToWatch="customer"
            />
          </v-card>
        </v-col>
      </v-row>
    </v-form>
  </v-container>
</template>

<script>
import CustomerService from "@/modules/core/services/customer-service";
import CountryService from "@/modules/core/services/country-service";
import DataUtils from "@/modules/core/services/data-utils";
import Customer from "@/modules/core/data/customer"
import GPSUtils from "@/modules/core/services/gps-utils";
import TwoButtonDialog from "@/modules/core/components/TwoButtonDialog/TwoButtonDialog";
import {ruleRequired} from "@/modules/core/services/rules";
import Utils from "@/modules/core/services/utils";
import TagComponent from '@/modules/core/components/TagComponent';
import BaseButton from "@/modules/core/components/Base/BaseButton";
import DirtyConfirmButtonDialog from "@/modules/core/components/TwoButtonDialog/DirtyConfirmButtonDialog";
import DuplicateCheckField from "@/modules/core/components/DuplicateCheckField";
import DuplicateCheckService from "@/modules/core/services/duplicate-check-service";
import ServerOptionForm from '@/modules/core/components/ServerOptionForm';
import UsersTab from "./UsersTab";

export default {
  name: "CustomerEditSettings",
  data() {
    return {
      id: 0,
      edit: null,
      customer: new Customer(),
      restCountry: [],
      restProvince: [],
      restCustomer: [],
      currentTab: null,
    }
  },
  watch: {
    currentLang() {
      Utils.sleep(500).then(() => {
        this.getAllCountries();
        this.updateProvince();
        this.$refs.form.resetValidation()
      });
    }
  },
  computed: {
    currentLang() {
      return this.$store.state.translation.language;
    }
  },
  components: {
    DuplicateCheckField,
    TwoButtonDialog,
    BaseButton,
    DirtyConfirmButtonDialog,
    TagComponent,
    ServerOptionForm,
    UsersTab
  },
  methods: {
    ruleRequired,

    getAllCustomers() {
      CustomerService.readAllFromAPI(
          (res) => {
            this.restCustomer = DataUtils.parseDataToObjectArray(res.data, Customer);
            this.$refs.cancelButton.startWatch();
          },
          (error) => {
            this.$logger.error("Error at CustomerService.readAllFromAPI" + error)
            Utils.showFailureSnackbar(this.$t('snackbar:load-failure', {name: this.$t('common:customer_plural')}));
          })
    },

    updateProvince() {
      if (this.customer.country) {

        CountryService.readProvincesOfCountry(this.customer.country,
            (res) => {
              this.restProvince = res.data;
              this.translateProvinces();
            },
            (error) => {
              Utils.showFailureSnackbar(this.$t('snackbar:load-failure', {name: this.$t('common:province_plural')}))
              this.$logger.error(error);
            }
        )
      }
    },
    getCustomer(id) {
      CustomerService.readFromAPI(id,
          (res) => {
            this.customer = DataUtils.parseDataToSingleObject(res.data, Customer);
            this.updateProvince();
            this.updateInfo();
            this.$refs.serverOptionForm.initialiseOptionsWithValues(this.customer.options);
          },
          (error) => {
            this.$logger.info("Error at fetching specific customer: " + error);
            this.$router.push('/403');
          }
      )
    },
    updateForm() {
      if (!this.$refs.form.validate()) {
        // TODO scroll to specific form field which doesnt require the rules
        window.scrollTo(0, 0);
        return;
      }
      let sendData = this.updateValueBeforeSave(Object.assign({}, this.customer));

      if (!this.edit) {
        CustomerService.postToAPI(sendData,
            () => {
              Utils.showSuccessSnackbar(this.$t('snackbar:add-success', {name: this.$t('common:customer')}));
              this.$router.push("/" + this.$store.state.manager.settingsModuleUrl + "/customer");
            },
            (error) => {
              Utils.showFailureSnackbar(this.$t('snackbar:add-failure', {name: this.$t('common:customer')}))
              this.$logger.error(error);
            }
        );
      } else {
        CustomerService.putToAPI(sendData,
            () => {
              Utils.showSuccessSnackbar(this.$t('snackbar:edit-success', {name: this.$t('common:customer')}));
              this.$router.push("/" + this.$store.state.manager.settingsModuleUrl + "/customer");
            },
            (error) => {
              Utils.showFailureSnackbar(this.$t('snackbar:edit-failure', {name: this.$t('common:customer')}))
              this.$logger.error(error);
            }
        );
      }
    },
    translateCountry() {
      this.restCountry = Utils.translateAllCountries(this.restCountry);
    },
    translateProvinces() {
      this.restProvince = Utils.translateAllProvinces(this.restCountry, this.restProvince, this.customer.country);
    },
    getAllCountries() {
      CountryService.readAllFromAPI(
          (res) => {
            this.restCountry = res.data;
            this.translateCountry();

            if (!this.edit) {
              this.customer.country = res.data[0].id;
              this.updateProvince();
            }
          },
          (error) => {
            Utils.showFailureSnackbar(this.$t('snackbar:load-failure', {name: this.$t('common:country')}))
            this.$logger.error(error);
          }
      )
    },
    deleteCustomer() {
      CustomerService.deleteFromAPI(this.customer,
          () => {
            Utils.showSuccessSnackbar(this.$t('snackbar:delete-success', {name: this.$t('common:customer')}));
            // Because of general Tabs, e.g. techn. Documentation, $router.go(-1) won't work
            // Example: Your last page was techn. Documentation, then you switch to facility tab
            // and delete facility - because of $router.go(-1), it would redirect back to
            // techn. documentation of the facility, but the facility doesn't exists anymore -> 404
            this.$router.push("/" + this.$store.state.manager.settingsModuleUrl + "/customer");
          },
          (error) => {
            Utils.showFailureSnackbar(this.$t('snackbar:delete-failure', {name: this.$t('common:customer')}));
            this.$refs.deleteButton.showDialog = false;
            this.$logger.error(error);
          }
      )
    },
    updateInfo() {
      if (this.customer.provision > 0) {
        this.customer.provision *= 100;
      }
    },
    getCorrectPercentage(value) {
      let result = value;

      if (isNaN(value)) {
        return '';
      } else if (value < 0) {
        result = 0;
      } else if (value > 100) {
        result = 100;
      }

      if (!Number.isInteger(result)) {
        result = Math.trunc(result);
      }

      return result;
    },
    updateValueBeforeSave(sendData) {
      if (sendData.provision > 0) {
        sendData.provision /= 100;
      }
      sendData.inhouse = !sendData.inhouse ? 0 : 1;
      sendData.advertiser = !sendData.advertiser ? 0 : 1;
      sendData.facilityOperator = !sendData.facilityOperator ? 0 : 1;

      return sendData;
    },

    /**
     * Reads and sets the building city and province
     * by the entered ZIP
     */
    getCityProvinceByZIP() {
      // check if there is a zip and country set for the building
      if (this.customer.zip && this.customer.country) {

        const countryName = GPSUtils.getCountryNameById(this.customer.country, this.restCountry);

        if (GPSUtils.isValidZIP(countryName.toLowerCase(), this.customer.zip)) {
          GPSUtils.readCityProvinceByZIP(
              this.customer.zip,
              countryName,
              this.$store.state.translation.language,
              this.restProvince,
              (res) => {
                this.customer.city = res.city;
                this.customer.province = res.province;
              },
              () => {
                Utils.showFailureSnackbar(this.$t('settings/customer:edit.zip-to-city-fail'))
              }
          )
        }
      }
    },

    checkByName(term) {
      DuplicateCheckService.checkCustomers('NAME', term,
          (res) => {
            this.$refs.duplName.fillData(res.data, this.$route.params.id);
          }
      )
    },

    checkByAddress(term) {
      DuplicateCheckService.checkCustomers('ADDRESS', term,
          (res) => {
            this.$refs.duplAddress.fillData(res.data, this.$route.params.id);
          }
      )
    },

    showCustomer(item) {
      this.getCustomer(item.id);
    },

    /**
     * Method that gets called whenever options are getting updated by ServerOptionForm.
     *
     * @param options updated options value, contains all option entries
     */
    updateOptions(options){
      this.customer.options = options;
    }
  },
  created() {
    this.getAllCountries();

    if (this.$route.params.id !== undefined) {
      this.edit = true;
      this.id = this.$route.params.id;
      this.getCustomer(this.id);
    } else {
      this.edit = false;
      this.customer.inhouse = true;
    }

    this.getAllCustomers();
  },
  mounted() {
    this.$refs.cancelButton.startWatch();
  }
}
</script>
<style src="@/modules/core/components/TwoButtonDialog/TwoButtonDialog.scss" lang="scss"/>
